import { supabase } from "../lib/supabase";
import { organizationService } from "./organizationService";

// Cache for user profiles to avoid redundant database calls
const userProfileCache = new Map<string, any>();

export const userService = {
  /**
   * Get a user's profile by ID
   * @param userId The user ID
   * @returns The user profile or null if not found
   */
  async getUserProfile(userId: string) {
    // Check cache first
    if (userProfileCache.has(userId)) {
      return userProfileCache.get(userId);
    }

    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", userId)
        .single();

      if (error) {
        console.error("Error fetching user profile:", error);
        return null;
      }

      // Cache the result
      userProfileCache.set(userId, data);
      return data;
    } catch (error) {
      console.error("Error in getUserProfile:", error);
      return null;
    }
  },

  /**
   * Get a user's display name by ID
   * @param userId The user ID
   * @returns The user's full name or the user ID if not found
   */
  async getUserDisplayName(userId: string) {
    const profile = await this.getUserProfile(userId);

    if (profile && profile.full_name) {
      return profile.full_name;
    }

    // If no profile or no name, return a formatted version of the ID
    return `User ${userId.substring(0, 8)}...`;
  },

  /**
   * Update a user's current organization
   * @param userId The user ID
   * @param organizationId The organization ID to set as current
   * @returns Success status
   */
  async updateCurrentOrganization(userId: string, organizationId: string) {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ current_organization_id: organizationId })
        .eq("id", userId);

      if (error) {
        console.error("Error updating current organization:", error);
        return false;
      }

      // Clear the cache for this user
      userProfileCache.delete(userId);

      return true;
    } catch (error) {
      console.error("Error in updateCurrentOrganization:", error);
      return false;
    }
  },

  /**
   * Get pending organization invitations for a user
   * @param userId The user ID
   * @returns Array of pending invitations
   */
  async getPendingInvitations(userId: string) {
    try {
      return await organizationService.getUserInvitations(userId);
    } catch (error) {
      console.error("Error in getPendingInvitations:", error);
      return [];
    }
  },

  /**
   * Accept an organization invitation
   * @param invitationId The invitation ID
   * @param userId The user ID accepting the invitation
   * @returns Success status
   */
  async acceptInvitation(invitationId: string, userId: string) {
    try {
      // First verify that this invitation belongs to the user
      const { data, error } = await supabase
        .from("organization_invitations")
        .select("*")
        .eq("id", invitationId)
        .eq("user_id", userId)
        .single();

      if (error || !data) {
        console.error("Error verifying invitation:", error);
        return false;
      }

      // Accept the invitation
      const success = await organizationService.acceptInvitation(invitationId);
      return success;
    } catch (error) {
      console.error("Error in acceptInvitation:", error);
      return false;
    }
  },

  /**
   * Decline an organization invitation
   * @param invitationId The invitation ID
   * @param userId The user ID declining the invitation
   * @returns Success status
   */
  async declineInvitation(invitationId: string, userId: string) {
    try {
      // First verify that this invitation belongs to the user
      const { data, error } = await supabase
        .from("organization_invitations")
        .select("*")
        .eq("id", invitationId)
        .eq("user_id", userId)
        .single();

      if (error || !data) {
        console.error("Error verifying invitation:", error);
        return false;
      }

      // Decline the invitation
      const success = await organizationService.declineInvitation(invitationId);
      return success;
    } catch (error) {
      console.error("Error in declineInvitation:", error);
      return false;
    }
  },

  /**
   * Clear the user profile cache
   */
  clearCache() {
    userProfileCache.clear();
  },
};
